import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { BreadcrumbList } from 'schema-dts';
import { helmetJsonLdProp } from 'react-schemaorg';
import {
  TContentHubNavigationBarBreadcrumbItem,
  TContentHubNavigationBarFields,
  TContentHubNavigationBarProps,
  TNavigationBarParams,
} from '../types/productBreadcrumbBar.types';
import { TJsonLDObj } from '../../../types/sitecore.types';
import { TSitecoreContext } from '../../../types/types';

const getBreadCrumbAbsoluteUrls = (origin = '', itemUrl = ''): string => {
  // function to remove the virtual folder name from origin as it is also appearing in breadcrumb relative url
  // and form an absolute url
  const itemSubRouteNames = itemUrl.split('/');
  const removeStrings: string[] = [];
  origin
    .replace('https://', '')
    .split(/\//)
    .forEach((value) => value && itemSubRouteNames.includes(value) && removeStrings.push(value));
  const filteredOrigin = removeStrings.reduce((acc, value) => acc.replace('/' + value, ''), origin);
  return filteredOrigin + itemUrl;
};

export class ContentHubNavigationBarModel {
  private readonly fields?: TContentHubNavigationBarFields;
  private readonly context?: TSitecoreContext;
  private readonly rendering?: ComponentRendering;
  private readonly params?: TNavigationBarParams;

  constructor(props: TContentHubNavigationBarProps) {
    this.fields = props?.fields;
    this.context = props?.sitecoreContext;
    this.rendering = props?.rendering;
    this.params = props?.params;
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get breadcrumbItems(): Array<TContentHubNavigationBarBreadcrumbItem> {
    const productItem: TContentHubNavigationBarBreadcrumbItem = {
      text: this.productsPageLabel || '',
      url: this.productsPageUrl || '',
    };

    const apiBreadcrumbItems = this.fields?.apiData?.breadcrumbs || [];

    return [productItem, ...apiBreadcrumbItems];
  }

  get productsPageLabel(): string | undefined {
    return this.fields?.itemData?.fields?.productsPageTitle;
  }

  get productsPageUrl(): string | undefined {
    return this.fields?.itemData?.fields?.productsPageLink;
  }

  get backButtonLabel(): string | undefined {
    return this.fields?.itemData?.fields?.backButtonText;
  }

  get backButtonUrl(): string | undefined {
    return this.fields?.itemData?.fields?.backButtonUrl;
  }

  get originUrl(): string {
    return this.context?.hostVirtualFolder || '';
  }

  get textColor(): string {
    return this.params?.textColor?.toLowerCase() || 'white';
  }

  get breadcrumbJSONLD(): TJsonLDObj {
    return helmetJsonLdProp<BreadcrumbList>({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: this.breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.text,
        item: getBreadCrumbAbsoluteUrls(this.originUrl, item.url),
      })),
    });
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }
}
